import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { camerasApi, type Camera } from '../services/camerasApi';
import {
  isCameraEnabledForEvidence,
  isUsableThumbnailSrc,
  OVERVIEW_CAMERA_THUMBNAIL_LIMIT,
  sortCamerasForThumbnailFetch,
} from '../domain/overview/overviewCameraEvidence';

export type OverviewCameraEvidenceState = {
  cameras: Camera[];
  thumbnails: Record<string, string>;
  listLoading: boolean;
  thumbnailsLoading: boolean;
  listNotice: string | null;
  refresh: () => void;
};

export type OverviewRequiredCamera = {
  cameraId: string;
  deviceId: string | null;
};

type UseOverviewCameraEvidenceArgs = {
  siteId: string;
  linkedByCamera?: Record<string, number>;
  /** Fetch these camera IDs even if bootstrap is empty or still loading. */
  requiredCameras?: OverviewRequiredCamera[];
  enabled?: boolean;
};

function requiredCameraKey(requiredCameras: OverviewRequiredCamera[]): string {
  return requiredCameras
    .map((camera) => `${camera.cameraId}:${camera.deviceId || ''}`)
    .sort()
    .join('|');
}

function linkedCameraKey(linkedByCamera: Record<string, number>): string {
  return Object.keys(linkedByCamera).sort().join('|');
}

export function useOverviewCameraEvidence({
  siteId,
  linkedByCamera = {},
  requiredCameras = [],
  enabled = true,
}: UseOverviewCameraEvidenceArgs): OverviewCameraEvidenceState {
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [listLoading, setListLoading] = useState(false);
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false);
  const [listNotice, setListNotice] = useState<string | null>(null);
  const fetchGenRef = useRef(0);
  const linkedByCameraRef = useRef(linkedByCamera);
  const requiredCamerasRef = useRef(requiredCameras);
  linkedByCameraRef.current = linkedByCamera;
  requiredCamerasRef.current = requiredCameras;
  const linkedKey = linkedCameraKey(linkedByCamera);
  const requiredKey = requiredCameraKey(requiredCameras);

  const activeCameras = useMemo(
    () => cameras.filter(isCameraEnabledForEvidence),
    [cameras],
  );

  const refresh = useCallback(() => {
    void linkedKey;
    void requiredKey;
    const generation = fetchGenRef.current + 1;
    fetchGenRef.current = generation;

    if (!enabled || !siteId) {
      setCameras([]);
      setThumbnails({});
      setListLoading(false);
      setThumbnailsLoading(false);
      setListNotice(siteId ? null : 'Select org/site scope to load camera evidence.');
      return;
    }

    const required = requiredCamerasRef.current.filter((camera) => camera.cameraId);
    const linked = linkedByCameraRef.current;
    void linkedKey;
    void requiredKey;
    setListLoading(true);
    setThumbnailsLoading(true);
    setListNotice(null);

    const fetchThumbnail = async (cameraId: string, deviceId: string) => {
      if (generation !== fetchGenRef.current) return;
      try {
        const snap = await camerasApi.getCameraThumbnail(cameraId, deviceId);
        if (generation !== fetchGenRef.current) return;
        if (!isUsableThumbnailSrc(snap)) return;
        setThumbnails((prev) => ({ ...prev, [cameraId]: snap }));
      } catch {
        // Per-camera failure only; Overview must not block.
      }
    };

    const loadRemainingThumbnails = async (items: Camera[], alreadyQueued: Set<string>) => {
      const extras = sortCamerasForThumbnailFetch(items, linked)
        .filter((cam) => !alreadyQueued.has(cam.camera_id))
        .slice(0, Math.max(0, OVERVIEW_CAMERA_THUMBNAIL_LIMIT - alreadyQueued.size));
      for (let i = 0; i < extras.length; i += 1) {
        if (generation !== fetchGenRef.current) return;
        if (i > 0 || alreadyQueued.size > 0) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
        if (generation !== fetchGenRef.current) return;
        await fetchThumbnail(extras[i].camera_id, extras[i].edge_device_id || '');
      }
    };

    void (async () => {
      const requiredIds = new Set(required.map((camera) => camera.cameraId));
      const requiredFetch = Promise.all(
        required.map((camera) => fetchThumbnail(camera.cameraId, camera.deviceId || '')),
      );
      try {
        const response = await camerasApi.listCameraBootstrap(siteId);
        if (generation !== fetchGenRef.current) return;
        if (!response?.ok) {
          setCameras([]);
          setListNotice('Camera list unavailable.');
          await requiredFetch;
          return;
        }
        const items = Array.isArray(response.items) ? response.items : [];
        setCameras(items);
        await requiredFetch;
        if (generation !== fetchGenRef.current) return;
        await loadRemainingThumbnails(items, requiredIds);
      } catch {
        if (generation !== fetchGenRef.current) return;
        setCameras([]);
        setListNotice('Camera list unavailable.');
        await requiredFetch;
      } finally {
        if (generation === fetchGenRef.current) {
          setListLoading(false);
          setThumbnailsLoading(false);
        }
      }
    })();
  }, [enabled, linkedKey, requiredKey, siteId]);

  useEffect(() => {
    refresh();
    return () => {
      fetchGenRef.current += 1;
    };
  }, [refresh]);

  return {
    cameras: activeCameras,
    thumbnails,
    listLoading,
    thumbnailsLoading,
    listNotice,
    refresh,
  };
}
